import type { MigrationPreparationJobState } from "~/gql/graphqlBase.generated";
import type { ImportQuestion } from "~/helpers/aiChat/migrationReview";

import { Box } from "@mui/material";
import { useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";

import FernieLoadError from "~/components/fernie/FernieLoadError";
import LINE_SX from "~/components/fernie/fernieStyles";
import { CaptionText } from "~/components/Typographies";
import migrationReview from "~/helpers/aiChat/migrationReview";
import { useGetMigrationImportQuestionsQuery } from "~/services/endpoints/apiEndpoints";

interface FernieImportQuestionsProps {
	jobState: MigrationPreparationJobState;
	journeyId: string;
	sourceId: string;
}

interface QuestionPageProps {
	journeyId: string;
	skip: number;
	sourceId: string;
}

const LIST_SX = { display: "flex", flexDirection: "column", gap: 0.5, listStyle: "none", m: 0, p: 0 } as const;

interface QuestionItemProps {
	question: ImportQuestion;
}

const QuestionItem = ({ question }: QuestionItemProps) => {
	const { t } = useTranslation("common");
	const status = migrationReview.questionStatus(question);
	const coverage = migrationReview.groupedAnswerCoverage(question);

	return (
		<Box component="li" sx={{ borderColor: "divider", borderLeft: "2px solid", pl: 1 }}>
			<CaptionText sx={{ ...LINE_SX, fontWeight: 500 }}>{migrationReview.inertCellText(question.text)}</CaptionText>
			<CaptionText sx={{ ...LINE_SX, color: "text.secondary" }}>
				{t("fernieChat.review.questions.affected", { count: question.affectedCount })}
			</CaptionText>
			{status === "answered" ? (
				<>
					<CaptionText sx={LINE_SX}>
						{t("fernieChat.review.questions.answered", {
							answer: migrationReview.inertCellText(migrationReview.answerLabel(question) ?? "")
						})}
					</CaptionText>
					<CaptionText sx={LINE_SX}>{t("fernieChat.review.questions.covers", { count: coverage.covered })}</CaptionText>
					{coverage.exceptions > 0 && (
						<CaptionText sx={LINE_SX}>
							{t("fernieChat.review.questions.exceptions", { count: coverage.exceptions })}
						</CaptionText>
					)}
					<CaptionText sx={{ ...LINE_SX, color: "text.secondary" }}>
						{t("fernieChat.review.questions.savedNote")}
					</CaptionText>
				</>
			) : (
				<>
					<Box component="ul" sx={{ m: 0, pl: 2 }}>
						{question.options.map((option) => (
							<CaptionText component="li" key={option.key} sx={LINE_SX}>
								{option.recordCount === null || option.recordCount === undefined
									? migrationReview.inertCellText(option.label)
									: t("fernieChat.review.questions.optionWithCount", {
											count: option.recordCount,
											label: migrationReview.inertCellText(option.label)
										})}
							</CaptionText>
						))}
					</Box>
					<CaptionText sx={{ ...LINE_SX, color: "warning.main" }}>
						{t(
							status === "earlierAnswer"
								? "fernieChat.review.questions.earlierAnswer"
								: "fernieChat.review.questions.open"
						)}
					</CaptionText>
				</>
			)}
			{question.examples.length > 0 && (
				<>
					<CaptionText sx={{ ...LINE_SX, color: "text.secondary" }}>
						{t("fernieChat.review.questions.examples")}
					</CaptionText>
					<Box component="ul" sx={{ m: 0, pl: 2 }}>
						{question.examples.map((example) => (
							<CaptionText component="li" key={example.row} sx={LINE_SX}>
								{migrationReview.exampleText(example)}
							</CaptionText>
						))}
					</Box>
				</>
			)}
		</Box>
	);
};

const QuestionPage = ({ journeyId, skip, sourceId }: QuestionPageProps) => {
	const { currentData, isError, refetch } = useGetMigrationImportQuestionsQuery(
		migrationReview.questionPageArgs(journeyId, sourceId, skip)
	);
	const page = currentData?.migrationImportQuestions;
	if (isError) return <FernieLoadError onRetry={refetch} />;
	if (!page) return null;
	const nextSkip = skip + migrationReview.questionPageSize;

	return (
		<>
			{migrationReview.currentQuestions(page.questions).map((question) => (
				<QuestionItem key={question.questionId} question={question} />
			))}
			{nextSkip < page.total && <QuestionPage journeyId={journeyId} skip={nextSkip} sourceId={sourceId} />}
		</>
	);
};

const FernieImportQuestions = ({ jobState, journeyId, sourceId }: FernieImportQuestionsProps) => {
	const { t } = useTranslation("common");
	const titleId = useId();
	const { currentData, isError, refetch } = useGetMigrationImportQuestionsQuery(
		migrationReview.questionPageArgs(journeyId, sourceId, 0)
	);
	const seenJobState = useRef(jobState);

	useEffect(() => {
		if (seenJobState.current === jobState) return;
		seenJobState.current = jobState;
		void refetch();
	}, [jobState, refetch]);

	const firstPage = currentData?.migrationImportQuestions;
	if (isError) return <FernieLoadError onRetry={refetch} />;
	if (!firstPage || !migrationReview.showsQuestions(firstPage)) return null;

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.5 }}>
			<CaptionText bold id={titleId} sx={LINE_SX}>
				{t("fernieChat.review.questions.title")}
			</CaptionText>
			<Box aria-labelledby={titleId} component="ul" sx={LIST_SX}>
				<QuestionPage journeyId={journeyId} key={jobState} skip={0} sourceId={sourceId} />
			</Box>
		</Box>
	);
};

export default FernieImportQuestions;
